import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, Optional } from '@angular/core';
import { Filter } from '@df/catalog-ui/listing/filter/filter';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Client, ISizeName } from '@df/core/service/client';
import { DfModal } from '@df/ui/modal/df-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Subscription } from 'rxjs';
import { ButtonStyleComponent } from '../../common/buttons/button-style/button-style.component';
import { NModalLayoutRight } from '../../common/modal/layout-right/modal-layout-right.component';
import { Product } from '../product/product.decorator';
import { FilterAttributeOptionComponent } from './filter/filter-attribute-option.component';
import { ListingSortComponent } from './sort/listing-sort.component';

export namespace NProductListingFiltersModal {
    /**
     * Modal identifier
     */
    export const id = 'product-listing-filters';

    /**
     * Datapasset to modal
     */
    export interface IModalData {
        listing: ProductListing<Product<IProductDataV2>>;
        defaultFilter?: Filter<Product>;
    }
}

@DfModal({
    id: NProductListingFiltersModal.id,
    position: (sizeId: ISizeName) => (sizeId === 'S' || sizeId === 'M' ? 'bottom' : 'right'),
    config: sizeId => ({
        ...NModalLayoutRight.getConfig(sizeId),
        height: sizeId === 'S' || sizeId === 'M' ? '75%' : '100%',
        position: { bottom: '0', right: '0' }
    }),
    sizeRestriction: sizeId => ['S', 'M', 'L', 'X'],
    reopenPolicy: NDfModal.ReopenPolicy.first
})
@Component({
    templateUrl: './product-listing-filters.modal.html',
    imports: [TranslationModule, ListingSortComponent, FilterAttributeOptionComponent, ButtonStyleComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListingFiltersModalComponent implements NDfModal.IModalComponent, OnInit {
    /**
     * Modal option config
     */
    modalConfig?: NDfModal.IModalConfig;

    /**
     * Listing changes subscription
     */
    private listingSubscription?: Subscription;

    /**
     * Listing shorthand
     */
    get listing() {
        return this.data.listing;
    }

    constructor(
        readonly client: Client,
        private app: App,
        private changeDetectorRef: ChangeDetectorRef,
        @Optional() private modalRef: ModalRef<any>,
        @Optional() @Inject(MODAL_DATA) protected data: NProductListingFiltersModal.IModalData
    ) {
        this.changeDetectorRef.detach();
        modalRef.afterClosed().subscribe(() => {
            this.listingSubscription?.unsubscribe();
        });
    }

    /**
     * Initiate component
     */
    ngOnInit() {
        this.listingSubscription = this.listing.subscribe(() => this.detectChanges());
        this.detectChanges();
    }

    /**
     * Close modal
     */
    close() {
        this.modalRef.close();
    }

    get defaultFilter(): Filter<Product> | undefined {
        return this.data.defaultFilter;
    }

    /**
     * Change detection
     */
    private detectChanges() {
        this.changeDetectorRef.detectChanges();
    }
}
